import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, UserX, CalendarPlus, Hourglass, Image as ImageIcon, IdCard, Scale, CheckCircle2, UserPlus, UserRound } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/admin/PageHeader";
import { ActivityChart } from "../../components/admin/ActivityChart";
import { useAuth } from "../../context/AuthContext";
import { canDo } from "../../lib/adminApi";
import { supabase } from "../../lib/supabase";

const Stat = ({ icon: Icon, label, value, testId, to }) => {
  const inner = (
    <>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#7A0016] text-[#F5E6BA]"><Icon className="h-5 w-5" /></span>
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold uppercase tracking-wider text-[#8C6A6C]">{label}</p>
        <p data-testid={`${testId}-value`} className="font-display text-3xl font-bold text-[#4A000E]">{value ?? "-"}</p>
      </div>
    </>
  );
  if (to) return <Link to={to} data-testid={testId} className="rmm-card rmm-rise flex items-center gap-4 p-5 transition-shadow hover:shadow-xl">{inner}</Link>;
  return <div data-testid={testId} className="rmm-card rmm-rise flex items-center gap-4 p-5">{inner}</div>;
};

const ratio = (m, f) => {
  if (!m && !f) return "0 : 0";
  const g = (a, b) => (b ? g(b, a % b) : a);
  const d = g(m, f) || 1;
  return `${m / d} : ${f / d}`;
};

export default function Dashboard() {
  const auth = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => {
    supabase.rpc("admin_dashboard_stats").then(({ data, error }) => (error ? toast.error(error.message) : setS(data)));
  }, []);
  const qa = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors";
  return (
    <div data-testid="admin-dashboard">
      <PageHeader eyebrow="Overview" title="Dashboard">
        {canDo(auth, "can_approve") && <Link to="/admin/approvals" data-testid="qa-approve-pending" className={`${qa} bg-[#7A0016] text-[#F5E6BA] hover:bg-[#4A000E]`}><CheckCircle2 className="h-4 w-4" />Approve Pending Profiles</Link>}
        {(canDo(auth, "can_approve") || canDo(auth, "can_verify")) && <Link to="/admin/media" data-testid="qa-review-media" className={`${qa} border border-[#D4AF37] bg-white text-[#7A0016] hover:bg-[#F5E6BA]/50`}><ImageIcon className="h-4 w-4" />Review Photos &amp; ID</Link>}
        {canDo(auth, "can_edit") && <Link to="/admin/profiles/new" data-testid="qa-add-profile" className={`${qa} border border-[#D4AF37] bg-white text-[#7A0016] hover:bg-[#F5E6BA]/50`}><UserPlus className="h-4 w-4" />Add New Profile</Link>}
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="Total registered users" value={s?.total_users} testId="stat-total-users" />
        <Stat icon={UserCheck} label="Active profiles (approved)" value={s?.active_profiles} testId="stat-active" />
        <Stat icon={UserX} label="Inactive / blocked profiles" value={s?.inactive_profiles} testId="stat-inactive" />
        <Stat icon={CalendarPlus} label="Today's new registrations" value={s?.today_registrations} testId="stat-today" />
        <Stat icon={Hourglass} label="Pending profile approvals" value={s?.pending_profiles} testId="stat-pending-profiles" />
        <Stat icon={ImageIcon} label="Pending photo approvals" value={s?.pending_photos} testId="stat-pending-photos" />
        <Stat icon={IdCard} label="Pending ID proof reviews" value={s?.pending_ids} testId="stat-pending-ids" />
        <Stat icon={Scale} label="Gender ratio (M : F)" value={s ? ratio(s.male, s.female) : null} testId="stat-gender-ratio" />
        <Stat icon={UserRound} label="Brides" value={s?.female} testId="stat-brides" to="/admin/profiles/brides" />
        <Stat icon={UserRound} label="Grooms" value={s?.male} testId="stat-grooms" to="/admin/profiles/grooms" />
      </div>
      <div className="mt-6"><ActivityChart /></div>
    </div>
  );
}
